/**
 * Scheduling reads, all real: the ZOY-250 window list, personnel (for
 * practitioner columns), rooms, per-resource availability (calendar layers),
 * and per-id patient names (the booking payload carries ids only — a settled
 * decision; names are fetched and cached by id).
 */

import { useEffect, useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { listBookingsWindow } from '@/core/clinic/bookingsApi';
import { listGroupSessions } from '@/core/clinic/groupSessionsApi';
import {
  fetchPractitionerAvailability,
  fetchRoomAvailability,
  listAllPractitionerCapabilities,
  listPersonnel,
  listRooms,
  type PersonnelView,
  type RoomView,
} from '@/core/clinic/setupApi';
import { useAuthz } from '@/core/authz/AuthzProvider';
import { PERM } from '@/core/authz/permissions';
import { bookingFilters, type BookingWindowFilters } from './bookingRules';

export function useBookingsWindow(f: BookingWindowFilters | null) {
  const authz = useAuthz();
  const key = f ? JSON.stringify(bookingFilters(f)) : 'none';
  return useQuery({
    queryKey: ['bookings', 'window', authz.clinicId, key],
    queryFn: () =>
      listBookingsWindow({
        filters: bookingFilters(f!),
        sort: [{ field: 'startsAt', dir: 'asc' }],
      }),
    enabled: f !== null && authz.can(PERM.bookingsRead),
    staleTime: 30_000,
  });
}

/**
 * ZOY-685: the group occurrences of a window (no server filters — the
 * calendar's own apply client-side). Read beside the booking window so a
 * group renders ONCE, never one block per attendee.
 */
export function useGroupSessionsWindow(w: { from: Date; to: Date } | null) {
  const authz = useAuthz();
  return useQuery({
    queryKey: [
      'group-sessions',
      'window',
      authz.clinicId,
      w?.from.toISOString(),
      w?.to.toISOString(),
    ],
    queryFn: () => listGroupSessions(w!.from, w!.to),
    enabled: w !== null && authz.can(PERM.bookingsRead),
    staleTime: 30_000,
  });
}

/**
 * Who counts as a practitioner on a scheduling surface (calendar columns,
 * practitioner filters): someone linked to at least one service they can be
 * booked for — the same rule New booking uses to offer a practitioner.
 *
 * A practitioner PROFILE alone is not enough: the staff profile form creates
 * one for anyone whose card is saved, receptionists and accountants
 * included. When the service links cannot be read (no
 * clinic.capabilities.read) the profile stays the rule, as before — a
 * surface never goes blank for lack of a supporting read.
 */
export function practitionerRoster(
  personnel: readonly PersonnelView[],
  capableUids: ReadonlySet<string> | null,
): PersonnelView[] {
  return personnel.filter(
    (p) => p.practitioner !== null && (capableUids === null || capableUids.has(p.uid)),
  );
}

/** The practitioners of this clinic (personnel.read); empty when unreadable. */
export function usePractitioners(): {
  items: PersonnelView[];
  readable: boolean;
  loading: boolean;
} {
  const authz = useAuthz();
  const readable = authz.can(PERM.personnelRead);
  const capsReadable = authz.can(PERM.capabilitiesRead);
  const q = useQuery({
    queryKey: ['personnel', 'all', authz.clinicId],
    queryFn: () => listPersonnel({ limit: 100, sort: [{ field: 'uid', dir: 'asc' }] }),
    enabled: readable,
    staleTime: 5 * 60_000,
  });
  const caps = useQuery({
    queryKey: ['capabilities', 'practitioners', 'all', authz.clinicId],
    queryFn: listAllPractitionerCapabilities,
    enabled: readable && capsReadable,
    staleTime: 5 * 60_000,
  });
  const capableUids = useMemo(
    () =>
      capsReadable && caps.data
        ? new Set(caps.data.filter((c) => c.eligible !== false).map((c) => c.uid))
        : null,
    [capsReadable, caps.data],
  );
  // A failed link read keeps the profile rule rather than an empty roster.
  const waiting = capsReadable && caps.isPending;
  return {
    items: waiting
      ? []
      : practitionerRoster(q.data?.items ?? [], caps.isError ? null : capableUids),
    readable,
    loading: readable && (q.isPending || waiting),
  };
}

export function useRoomsList(): RoomView[] {
  const authz = useAuthz();
  const q = useQuery({
    queryKey: ['rooms', 'all', authz.clinicId],
    queryFn: () => listRooms({ limit: 100, sort: [{ field: 'name', dir: 'asc' }] }),
    enabled: authz.can(PERM.roomsRead),
    staleTime: 5 * 60_000,
  });
  return (q.data?.items ?? []) as RoomView[];
}

export function usePractitionerAvailabilityMap(uids: readonly string[]) {
  const authz = useAuthz();
  const enabled = authz.can(PERM.availabilityRead);
  const results = useQueries({
    queries: uids.map((uid) => ({
      queryKey: ['availability', authz.clinicId, uid],
      queryFn: () => fetchPractitionerAvailability(uid),
      enabled,
      // Weekly hours change rarely (Product 2026-10-14), and every edit made
      // in this panel invalidates this key (PractitionerAvailability) — so the
      // calendar and the booking form read them from memory for a working day
      // instead of asking again. An edit made on another device shows here
      // after a reload or once the day's copy expires.
      staleTime: 12 * 60 * 60_000,
      gcTime: 24 * 60 * 60_000,
    })),
  });
  return useMemo(() => {
    const m = new Map<
      string,
      {
        workingHours: { dayOfWeek: number; startMinute: number; endMinute: number }[];
        timeOff: { startsAt: string; endsAt: string; reason: string | null }[];
      }
    >();
    results.forEach((r, i) => {
      const uid = uids[i];
      if (uid && r.data) m.set(uid, r.data);
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results.map((r) => r.dataUpdatedAt).join(','), uids.join(',')]);
}

export function useRoomAvailabilityMap(roomIds: readonly string[]) {
  const authz = useAuthz();
  const enabled = authz.can(PERM.availabilityRead);
  const results = useQueries({
    queries: roomIds.map((id) => ({
      queryKey: ['room-availability', authz.clinicId, id],
      queryFn: () => fetchRoomAvailability(id),
      enabled,
      staleTime: 5 * 60_000,
    })),
  });
  return useMemo(() => {
    const m = new Map<
      string,
      {
        workingHours: { dayOfWeek: number; startMinute: number; endMinute: number }[];
        blockouts: { startsAt: string; endsAt: string; reason: string | null }[];
      }
    >();
    results.forEach((r, i) => {
      const id = roomIds[i];
      if (id && r.data) m.set(id, r.data);
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results.map((r) => r.dataUpdatedAt).join(','), roomIds.join(',')]);
}

/** id → "First Last" for the patients in view; ids are fetched once and cached. */

/** Narrow-screen detection for the agenda fallback (design: phone = list). */
export function useIsNarrow(maxPx = 767): boolean {
  const query = `(max-width: ${maxPx}px)`;
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return narrow;
}
